import { ReactNode, useContext } from 'react';
import { formatDuration } from 'date-fns';

import { ZoomRegistrationSuccess, ZoomWebinar } from 'types/zoom';

import { determineRecurringIntervalAndTime, getUserTimeZone } from 'components/content-blocks/webinar-registration/util/date-helpers';
import ZoomWebinarSignUpForm from 'components/forms/zoom-webinar';
import FontIcon from 'components/icons';

import { EmployeeCountOption, ScheduleDemoWizardContext } from '../schedule-demo-wizard';
import { ScheduleDemoMercuryAction } from '../util/send-mercury-event';

type MetaItemProps = {
  children: ReactNode
  icon: string
}

const MetaItem = ({
  children,
  icon,
}:MetaItemProps) => {
  return (
    <div className="meta-item">
      <div className="meta-item--icon">
        <FontIcon icon={ icon } />
      </div>
      <div className="meta-item--text">{children}</div>
    </div>
  );
};

type Props = {
  avatarImageUrl: string
  employeeCount: EmployeeCountOption | undefined
  onRegistration: () => void
  zoomWebinar: ZoomWebinar
}

const StepWebinarRegistration = ({
  avatarImageUrl,
  onRegistration,
  zoomWebinar,
}:Props) => {

  const { emitEvent } = useContext(ScheduleDemoWizardContext);

  if (!zoomWebinar.recurrence || !zoomWebinar.occurrences) {
    return null;
  }

  const recurrence = determineRecurringIntervalAndTime(zoomWebinar.recurrence, zoomWebinar.occurrences[0], true);

  const onSuccessfulRegistration = (registration: ZoomRegistrationSuccess, occurrenceId?: string | null) => {
    emitEvent({
      action: ScheduleDemoMercuryAction.WEBINAR_REGISTERED,
      formData: {
        formId: 'webinarRegistration',
        insensitiveFields: {
          companyName: registration.registrant.companyName ?? undefined,
          zoomWebinarOccurenceId: occurrenceId ?? undefined,
          zoomWebinarStartTime: registration.response.start_time.toString(),
          zoomWebinarId: registration.response.id,
        },
        sensitiveFields: {
          email: registration.registrant.email,
          firstName: registration.registrant.firstName,
          lastName: registration.registrant.lastName,
        },
      },
    });
    window.posthog?.capture('Click to register for webinar');
    onRegistration();
  };

  return (
    <div className="schedule-demo-wizard--webinar-registration">
      <div className="webinar-registration--details-panel">
        <div className="webinar-details--avatar">
          <img src={ avatarImageUrl } alt="Avatar" />
        </div>
        <div className="webinar-details--title">
          {zoomWebinar.topic}
        </div>
        <div className="webinar-details--meta">
          <MetaItem icon="clock">
            {formatDuration({ minutes: zoomWebinar.duration })}
          </MetaItem>
          <MetaItem icon="calendar">
            {recurrence.intervalValue} @ {recurrence.time}
          </MetaItem>
          <MetaItem icon="job-site">
            {getUserTimeZone('long')}
          </MetaItem>
        </div>
        <div className="webinar-details--description">
          <p>{zoomWebinar.agenda}</p>
        </div>
      </div>
      <div className="webinar-registration--form-panel">
        <h2 className="webinar-form--title">Tell us about yourself</h2>
        <div className="webinar-form--form">
          <ZoomWebinarSignUpForm
            occurrences={ zoomWebinar.occurrences ?? [] }
            onRegistrationSuccess={ onSuccessfulRegistration }
            submitButtonClassName="button is-rounded green-pill-button schedule-demo-webinar-registration-submit"
            submitButtonText="Register Now"
            webinarId={ zoomWebinar.id }
          />
        </div>
      </div>
    </div>
  );
};

export default StepWebinarRegistration;
